import type { ReactElement } from "react";
import { Text, Button } from "@olivahealth/oli-ui";
import { useMediaQuery } from "react-responsive";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useRouter } from "next/router";
import ResourceStatus from "@olivahealth/graphql-server/src/domain/value-objects/ResourceStatus";
import ResourceType from "@olivahealth/graphql-server/src/domain/value-objects/ResourceType";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Mousewheel, Navigation } from "swiper/modules";
import useTranslation from "../../../hooks/useTranslation";
import ResourceCard from "../ResourceCard";
import { PopularResourcesQuery as IQuery } from "./__generated__/PopularResourcesQuery.graphql";
import "swiper/css";

const PopularResourcesQuery = graphql`
  query PopularResourcesQuery(
    $maxResources: Int
    $filterBy: ResourcesFilterBy
  ) {
    popularResources(first: $maxResources, filterBy: $filterBy) {
      nodes {
        id
        ...ResourceCard_resource
      }
    }
  }
`;

interface PopularResourcesProps {
  showSeeAllButton?: boolean;
  maxResources?: number;
  onFavouriteToggle?: () => void;
}

export default function PopularResources({
  showSeeAllButton = false,
  maxResources = 3,
  onFavouriteToggle,
}: PopularResourcesProps): ReactElement {
  const { t } = useTranslation("selfCare", { keyPrefix: "popularResources" });
  const isTabletOrMobile = useMediaQuery({ query: "(max-width: 1024px)" });
  const { push } = useRouter();

  const { popularResources } = useLazyLoadQuery<IQuery>(PopularResourcesQuery, {
    maxResources,
    filterBy: {
      status: [ResourceStatus.PUBLISHED],
      type: [
        ResourceType.AUDIO,
        ResourceType.PODCAST,
        ResourceType.TALK,
        ResourceType.VIDEO,
      ],
    },
  });

  const handleSeeAllClick = () => {
    push("/self-guided");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <Text weight="bold" size="xl" color="white">
          {t("title")}
        </Text>
        {showSeeAllButton && (
          <Button variant="linkLight" onClick={handleSeeAllClick}>
            {t("seeAllCta")}
          </Button>
        )}
      </div>

      {isTabletOrMobile ? (
        <div>
          <Swiper
            grabCursor
            modules={[A11y, Mousewheel, Navigation]}
            mousewheel={{
              releaseOnEdges: true,
            }}
            spaceBetween={16}
            slidesPerView={1.5}
            threshold={20}
            className="pb-1"
          >
            {popularResources.nodes.map((resource) => (
              <SwiperSlide key={resource.id} style={{ height: "auto" }}>
                <ResourceCard
                  showMarkAsFavouriteButton
                  resource={resource}
                  onFavouriteToggle={onFavouriteToggle}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        <div
          className={`grid gap-4 ${
            maxResources === 4 ? "grid-cols-4" : "grid-cols-3"
          }`}
        >
          {popularResources.nodes.map((resource) => (
            <ResourceCard
              showMarkAsFavouriteButton
              key={resource.id}
              resource={resource}
              onFavouriteToggle={onFavouriteToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}
